import React from 'react';
import { STATUS_ORDER } from './constants';
import { formatCurrency } from '../../utils/formatters';
import { ChevronRightIcon, ChevronDownIcon, BuildingOffice2Icon } from '@heroicons/react/24/outline';

type CompanySplitRowsProps = {
  companies: any[];
  statuses: string[];
  gridStyle: any;
  expandedCompanies?: Set<string>;
  setExpandedCompanies?: React.Dispatch<React.SetStateAction<Set<string>>>;
  hasHotelSplit?: (manager: string, company: string) => boolean;
  getHotelSplit?: (manager: string, company: string) => any[];
};

export function CompanySplitRows({
  companies,
  statuses,
  gridStyle,
  expandedCompanies = new Set(),
  setExpandedCompanies = () => {},
  hasHotelSplit = () => false,
  getHotelSplit = () => [],
}: CompanySplitRowsProps) {
  // Only show 'Lost' and 'Booked' for realised revenue
  const realisedStatuses = statuses.filter(s => s === 'Lost' || s === 'Booked');

  return (
    <>
      {companies.map(companyRow => {
        const canExpand = hasHotelSplit(companyRow.manager, companyRow.company);
        const isExpanded = expandedCompanies.has(`${companyRow.manager}||${companyRow.company}`);
        return (
          <React.Fragment key={companyRow.company}>
            <div
              className="gap-2 px-3 py-1.5 text-xs text-gray-600 grid bg-gray-50 cursor-pointer items-center"
              style={gridStyle}
              onClick={() => {
                if (canExpand) {
                  setExpandedCompanies((prev: Set<string>) => {
                    const next = new Set(prev);
                    const key = `${companyRow.manager}||${companyRow.company}`;
                    if (next.has(key)) next.delete(key);
                    else next.add(key);
                    return next;
                  });
                }
              }}
            >
              <div className="font-medium flex items-center">
                {/* Always render a chevron for alignment, but faded if not expandable */}
                {canExpand ? (
                  isExpanded ? (
                    <ChevronDownIcon className="w-4 h-4 mr-1 text-blue-500 flex-shrink-0" />
                  ) : (
                    <ChevronRightIcon className="w-4 h-4 mr-1 text-blue-500 flex-shrink-0" />
                  )
                ) : (
                  <ChevronRightIcon className="w-4 h-4 mr-1 text-gray-300 flex-shrink-0" />
                )}
                {companyRow.company}
              </div>
              <div className="text-center font-medium">{companyRow.nbProjects}</div>
              {statuses.map(status => (
                <div key={"nb-"+status} className="text-center font-medium">{companyRow[status]?.nb || 0}</div>
              ))}
              <div className="text-center font-medium">{companyRow.totalGroups}</div>
              {statuses.map(status => (
                <div key={"est-"+status} className="text-center text-gray-600">
                  {formatCurrency(companyRow[status]?.est || 0)}
                </div>
              ))}
              <div className="text-center text-gray-600 font-medium">
                {formatCurrency(companyRow.totalEst)}
              </div>
              <div className="text-center text-gray-600 font-medium">
                {formatCurrency(companyRow.totalReal)}
              </div>
              <div className="text-center text-gray-600 font-medium">
                {companyRow.Booked?.est > 0 ? Math.round((companyRow.totalReal / companyRow.Booked.est) * 100) + '%' : '-'}
              </div>
            </div>
            {isExpanded && (
              getHotelSplit(companyRow.manager, companyRow.company).map(hotelRow => (
                <div
                  key={hotelRow.venue}
                  className="gap-2 px-3 py-1.5 text-xs text-gray-500 grid bg-gray-100 border-l-4 border-blue-200 items-center"
                  style={gridStyle}
                >
                  <div className="font-medium pl-4 flex items-center">
                    <BuildingOffice2Icon className="w-4 h-4 mr-1 text-blue-400 flex-shrink-0" />
                    {hotelRow.venue}
                  </div>
                  <div className="text-center font-medium">{hotelRow.nbProjects}</div>
                  {statuses.map(status => (
                    <div key={"nb-"+status} className="text-center font-medium">{hotelRow[status]?.nb || 0}</div>
                  ))}
                  <div className="text-center font-medium">{hotelRow.totalGroups}</div>
                  {statuses.map(status => (
                    <div key={"est-"+status} className="text-center text-gray-600">
                      {formatCurrency(hotelRow[status]?.est || 0)}
                    </div>
                  ))}
                  <div className="text-center text-gray-600 font-medium">
                    {formatCurrency(hotelRow.totalEst)}
                  </div>
                  <div className="text-center text-gray-600 font-medium">
                    {formatCurrency(hotelRow.totalReal)}
                  </div>
                  <div className="text-center text-gray-600 font-medium">
                    {hotelRow.Booked?.est > 0 ? Math.round((hotelRow.totalReal / hotelRow.Booked.est) * 100) + '%' : '-'}
                  </div>
                </div>
              ))
            )}
          </React.Fragment>
        );
      })}
    </>
  );
} 